import { GRENADE, OBJECTIVES, PACK } from './constants';
import { getArmorProfile, otherTeam, type TeamId } from './game';
import { DEFAULT_MAP_ID, getMapData, type MapId } from './mapData';
import { terrainHeightAtMap } from './terrain';
import { routeZoneCallsign, routeZoneLabel } from './teamplay';
import type {
  BoostPadSnapshot,
  CtfMacroSnapshot,
  FlagSnapshot,
  GeneratorSnapshot,
  InventoryStationSnapshot,
  MatchEvent,
  PlayerScoreboardSnapshot,
  PlayerSnapshot,
  RepairZoneSnapshot,
  RouteCaptureZoneSnapshot,
  SensorSnapshot,
  ShieldDoorSnapshot,
  TurretSnapshot,
  VehicleSnapshot,
  WorldSnapshot,
} from './protocol';

export type StandaloneSessionOptions = {
  displayName?: string;
  preferredTeam?: 'auto' | TeamId;
  preferredMap?: MapId;
  botPreset?: 'training' | 'skirmish' | 'full';
};

export type StandaloneWorldSnapshot = WorldSnapshot & {
  packs: [];
};

const BOT_PRESET_COUNTS: Record<NonNullable<StandaloneSessionOptions['botPreset']>, number> = {
  training: 4,
  skirmish: 8,
  full: 12,
};

function normalizeMapId(value: unknown): MapId {
  return value === 'dangerouscrossing' || value === 'raindance' || value === 'katabatic' ? value : DEFAULT_MAP_ID;
}

function normalizeTeam(value: unknown): TeamId {
  return value === 'blue' ? 'blue' : 'red';
}

function terrainPosition(mapId: MapId, x: number, z: number, lift = 1) {
  return { x, y: terrainHeightAtMap(mapId, x, z) + lift, z };
}

function anchorPosition(mapId: MapId, x: number, z: number, lift = 2) {
  return { x, y: Math.max(terrainHeightAtMap(mapId, x, z) + lift, 12), z };
}

function yawToward(from: { x: number; z: number }, to: { x: number; z: number }) {
  return Math.atan2(to.x - from.x, to.z - from.z);
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function createFlag(team: TeamId, mapId: MapId): FlagSnapshot {
  const position = getMapData(mapId).flagPositions[team];
  return {
    id: `${team}-flag`,
    team,
    homePosition: { ...position },
    position: { ...position },
    holderId: null,
    state: 'home',
    returnTimerSeconds: 0,
  };
}

function createBoostPads(mapId: MapId): BoostPadSnapshot[] {
  const map = getMapData(mapId);
  const redHome = map.flagPositions.red;
  const blueHome = map.flagPositions.blue;
  const spread = map.baseProfile.spread;
  return [
    { id: 'mid-boost', position: anchorPosition(mapId, 0, 0, 0.35), radius: 8, active: true, cooldownSeconds: 0 },
    {
      id: 'red-flank',
      position: anchorPosition(mapId, redHome.x * 0.35, 18 * spread, 0.35),
      radius: 8,
      active: true,
      cooldownSeconds: 0,
    },
    {
      id: 'blue-flank',
      position: anchorPosition(mapId, blueHome.x * 0.35, -18 * spread, 0.35),
      radius: 8,
      active: true,
      cooldownSeconds: 0,
    },
  ];
}

function createRouteCaptureZones(mapId: MapId): RouteCaptureZoneSnapshot[] {
  const map = getMapData(mapId);
  const routes = map.routes;
  const routeForType = (types: string[]) => routes.find((route) => types.includes(route.type));
  const redHome = map.flagPositions.red;
  const blueHome = map.flagPositions.blue;
  const centerX = lerp(redHome.x, blueHome.x, 0.5);
  const centerZ = lerp(redHome.z, blueHome.z, 0.5);
  const radius = OBJECTIVES.flagPickupRadius * 1.8;

  const placements = [
    { id: 'route-mid', route: routeForType(['main']), x: centerX, z: centerZ },
    { id: 'route-red-approach', route: routeForType(['stealth']), x: lerp(redHome.x, blueHome.x, 0.33), z: centerZ + 17 },
    { id: 'route-blue-approach', route: routeForType(['chase']), x: lerp(redHome.x, blueHome.x, 0.67), z: centerZ - 17 },
    { id: 'route-high', route: routeForType(['high']), x: centerX, z: centerZ + 34 },
    { id: 'route-low', route: routeForType(['low']), x: centerX, z: centerZ - 34 },
  ];

  return placements.map((zone) => ({
    id: zone.id,
    label: zone.route?.label ?? routeZoneLabel(zone.id),
    callsign: zone.route?.label?.toUpperCase() ?? routeZoneCallsign(zone.id),
    position: terrainPosition(mapId, zone.x, zone.z, 1.2),
    radius,
    ownerTeam: null,
    captureTargetTeam: null,
    captureProgress: 0,
    contested: false,
    carrierInside: false,
    scoringMultiplier: 1,
  }));
}

function createGenerators(mapId: MapId): GeneratorSnapshot[] {
  const positions = getMapData(mapId).generatorPositions;
  return (['red', 'blue'] as const).map((team) => ({
    id: `${team}-generator`,
    team,
    position: { ...positions[team] },
    radius: 8,
    active: true,
    health: 180,
    maxHealth: 180,
    respawnSeconds: 0,
  }));
}

function createBaseSystems(mapId: MapId) {
  const map = getMapData(mapId);
  const spread = map.baseProfile.spread;
  const baseX = Math.abs(map.flagPositions.red.x);

  const sensors: SensorSnapshot[] = [
    { id: 'red-sensor-service', team: 'red', position: terrainPosition(mapId, -baseX - 12, -12, 1.2), radius: 5, active: true, health: 180, maxHealth: 180, respawnSeconds: 0 },
    { id: 'blue-sensor-service', team: 'blue', position: terrainPosition(mapId, baseX + 12, 12, 1.2), radius: 5, active: true, health: 180, maxHealth: 180, respawnSeconds: 0 },
  ];
  const shieldDoors: ShieldDoorSnapshot[] = [
    { id: 'red-shield-door-service', team: 'red', position: terrainPosition(mapId, -baseX - 10, 4 * spread, 1.4), radius: 7, active: true, powered: true, health: 120, maxHealth: 120, destroyed: false },
    { id: 'blue-shield-door-service', team: 'blue', position: terrainPosition(mapId, baseX + 10, -4 * spread, 1.4), radius: 7, active: true, powered: true, health: 120, maxHealth: 120, destroyed: false },
  ];
  const inventoryStations: InventoryStationSnapshot[] = [
    { id: 'red-inventory-1', team: 'red', position: anchorPosition(mapId, -baseX - 6, -10 * spread, 2.1), radius: 6.5, powered: true },
    { id: 'red-inventory-2', team: 'red', position: anchorPosition(mapId, -baseX - 6, 10 * spread, 2.1), radius: 6.5, powered: true },
    { id: 'blue-inventory-1', team: 'blue', position: anchorPosition(mapId, baseX + 6, 10 * spread, 2.1), radius: 6.5, powered: true },
    { id: 'blue-inventory-2', team: 'blue', position: anchorPosition(mapId, baseX + 6, -10 * spread, 2.1), radius: 6.5, powered: true },
  ];
  const repairZones: RepairZoneSnapshot[] = [
    { id: 'red-repair-1', team: 'red', position: anchorPosition(mapId, -baseX + 8, 0, 0.5), radius: 9, powered: true },
    { id: 'blue-repair-1', team: 'blue', position: anchorPosition(mapId, baseX - 8, 0, 0.5), radius: 9, powered: true },
  ];
  const turrets: TurretSnapshot[] = [
    { id: 'red-turret-1', team: 'red', position: anchorPosition(mapId, -baseX + 4, -18 * spread, 4.2), radius: 5, active: true, networkState: 'online', yaw: 0.22, cooldownSeconds: 0 },
    { id: 'red-turret-2', team: 'red', position: anchorPosition(mapId, -baseX + 4, 18 * spread, 4.2), radius: 5, active: true, networkState: 'online', yaw: -0.22, cooldownSeconds: 0 },
    { id: 'blue-turret-1', team: 'blue', position: anchorPosition(mapId, baseX - 4, 18 * spread, 4.2), radius: 5, active: true, networkState: 'online', yaw: Math.PI - 0.22, cooldownSeconds: 0 },
    { id: 'blue-turret-2', team: 'blue', position: anchorPosition(mapId, baseX - 4, -18 * spread, 4.2), radius: 5, active: true, networkState: 'online', yaw: Math.PI + 0.22, cooldownSeconds: 0 },
  ];

  return { sensors, shieldDoors, inventoryStations, repairZones, turrets };
}

function createVehicles(mapId: MapId): VehicleSnapshot[] {
  const homes = getMapData(mapId).flagPositions;
  const makeVehicle = (id: string, team: TeamId, vehicleId: 'grav-bike' | 'assault-tank', offsetZ: number, health: number): VehicleSnapshot => {
    const home = homes[team];
    const x = home.x + (team === 'red' ? 14 : -14);
    const z = home.z + offsetZ;
    const position = terrainPosition(mapId, x, z, 1.6);
    return {
      id,
      team,
      vehicleId,
      position,
      homePosition: { ...position },
      velocity: { x: 0, y: 0, z: 0 },
      yaw: team === 'red' ? Math.PI / 2 : -Math.PI / 2,
      pitch: 0,
      roll: 0,
      health,
      maxHealth: health,
      occupiedBy: null,
      active: true,
      respawnSeconds: 0,
      returnSeconds: null,
      boostActive: false,
      boostCooldownSeconds: 0,
      boostRemainingSeconds: 0,
      weaponCooldown: 0,
      weaponId: vehicleId === 'grav-bike' ? 'vehicle-blaster' : 'vehicle-missile',
      ammo: vehicleId === 'grav-bike' ? -1 : 20,
    };
  };

  return [
    makeVehicle('red-grav-bike', 'red', 'grav-bike', -11, 120),
    makeVehicle('red-assault-tank', 'red', 'assault-tank', 11, 320),
    makeVehicle('blue-grav-bike', 'blue', 'grav-bike', -11, 120),
    makeVehicle('blue-assault-tank', 'blue', 'assault-tank', 11, 320),
  ];
}

function createPlayer(
  id: string,
  displayName: string,
  team: TeamId,
  mapId: MapId,
  slot: number,
  isBot: boolean,
): PlayerSnapshot {
  const armor = getArmorProfile('medium');
  const map = getMapData(mapId);
  const spawn = map.spawnPositions[team][slot % map.spawnPositions[team].length];
  const target = map.spawnViewTargets[team][slot % map.spawnViewTargets[team].length] ?? map.flagPositions[otherTeam(team)];

  return {
    id,
    position: { ...spawn },
    velocity: { x: 0, y: 0, z: 0 },
    yaw: yawToward(spawn, target),
    pitch: -0.06,
    health: armor.maxHealth,
    energy: armor.maxEnergy,
    speed: 0,
    score: 0,
    respawnTimer: 0,
    isBot,
    displayName,
    team,
    armorClass: 'medium',
    selectedWeapon: 'spinfusor',
    selectedGrenade: GRENADE.defaultId,
    packId: PACK.defaultId,
    packEnergy: PACK.maxEnergy,
    packCooldown: 0,
    packActiveSeconds: 0,
    grenadeCharges: GRENADE.maxCharges,
    grenadeCooldown: 0,
    weaponHeat: 0,
    weaponCooldown: 0,
    overheated: false,
    spawnProtected: false,
    carryingFlagTeam: null,
    kills: 0,
    deaths: 0,
    captures: 0,
    returns: 0,
    botRole: isBot ? (slot % 2 === 0 ? 'defender' : 'chaser') : undefined,
  };
}

function createBots(count: number, localTeam: TeamId, mapId: MapId) {
  const bots: PlayerSnapshot[] = [];
  for (let index = 0; index < count; index += 1) {
    const team = index % 2 === 0 ? otherTeam(localTeam) : localTeam;
    bots.push(createPlayer(`standalone-bot-${index + 1}`, `Bot ${index + 1}`, team, mapId, index + 1, true));
  }
  return bots;
}

function toScoreboardPlayer(player: PlayerSnapshot): PlayerScoreboardSnapshot {
  return {
    id: player.id,
    displayName: player.displayName,
    team: player.team,
    score: player.score,
    kills: player.kills,
    deaths: player.deaths,
    captures: player.captures,
    returns: player.returns,
    isBot: player.isBot,
  };
}

function createCtfMacro(): CtfMacroSnapshot {
  return {
    role: 'neutral',
    title: 'Route Control',
    status: 'Local simulation ready',
    cue: 'MID LANE LIVE',
    urgency: 'normal',
    routeZoneId: 'route-mid',
    flags: {
      red: { state: 'home', carrierId: null, returnTimerSeconds: 0 },
      blue: { state: 'home', carrierId: null, returnTimerSeconds: 0 },
    },
    telemetry: {
      red: { grabs: 0, returns: 0, captures: 0, carrierKills: 0, standoffs: 0, generatorDestroys: 0, generatorRepairs: 0 },
      blue: { grabs: 0, returns: 0, captures: 0, carrierKills: 0, standoffs: 0, generatorDestroys: 0, generatorRepairs: 0 },
    },
  };
}

export function createStandaloneWorldSnapshot(options: StandaloneSessionOptions = {}): StandaloneWorldSnapshot {
  const mapId = normalizeMapId(options.preferredMap);
  const localTeam = normalizeTeam(options.preferredTeam);
  const botCount = BOT_PRESET_COUNTS[options.botPreset ?? 'full'];
  const displayName = options.displayName?.replace(/\s+/g, ' ').trim().slice(0, 24) || 'You';
  const localPlayer = createPlayer('local-standalone', displayName, localTeam, mapId, 0, false);
  const bots = createBots(botCount, localTeam, mapId);
  const baseSystems = createBaseSystems(mapId);
  const routeCaptureZones = createRouteCaptureZones(mapId);
  const events: MatchEvent[] = [
    {
      id: 'standalone-ready',
      message: `${getMapData(mapId).label} lanes online: mid, high, low, and generator routes active.`,
      category: 'system',
      priority: 90,
    },
  ];

  return {
    serverTick: 1,
    mapId,
    players: [localPlayer, ...bots],
    scoreboardPlayers: [localPlayer, ...bots].map(toScoreboardPlayer),
    projectiles: [],
    match: {
      phase: 'warmup',
      teamScores: { red: 0, blue: 0 },
      winnerTeam: null,
      countdownSeconds: 180,
      remainingSeconds: 180,
      scoreToWin: 15,
      leaderName: 'Local simulation',
      awards: [],
    },
    flags: [createFlag('red', mapId), createFlag('blue', mapId)],
    boostPads: createBoostPads(mapId),
    generators: createGenerators(mapId),
    sensors: baseSystems.sensors,
    shieldDoors: baseSystems.shieldDoors,
    inventoryStations: baseSystems.inventoryStations,
    repairZones: baseSystems.repairZones,
    turrets: baseSystems.turrets,
    effects: [],
    vehicles: createVehicles(mapId),
    deployables: [],
    events,
    directives: [],
    tacticalPings: [],
    routeCaptureZones,
    objectiveScoringHints: [],
    baseFlagInteractionHooks: [],
    ctfMacro: createCtfMacro(),
    packs: [],
  };
}
